import { useState } from 'react';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import { AlertTriangle, Check, Inbox, MessageSquareQuote } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { get, post } from '@/lib/api';
import { formatRelative } from '@/lib/time';
import { errMsg, refreshSubmission } from '@/lib/submission-ui';
import {
  artworkFor, artworkGallery, blockReason, changeTone, changeVerb, confirmMessage,
  pendingProposals, promotedMessage, proposalHeadline, proposalKey,
  type ProposalChange, type ProposalsPayload, type SlotProposal,
} from '@/lib/slot-proposal';

// A REQUESTED decoration change waiting on a worker.
//
// The "Request changes" link in a proof email can add a print location, remove
// one, change what a location prints, or attach new artwork — but it cannot
// APPLY any of that: its holder is the customer, not a GhostFit worker, and a
// live decoration change is something that gets proofed, approved and printed.
// So requestedit records the change and stops, and this panel is where a human
// looks at it and applies it.
//
// Deliberately loud, and deliberately above the tier cards: an unapplied
// structural request means the customer is waiting on us.

const TONE: Record<ReturnType<typeof changeTone>, string> = {
  add: 'bg-success/10 text-success',
  remove: 'bg-danger/10 text-danger',
  artwork: 'bg-primary/10 text-primary',
  kind: 'bg-warning/10 text-warning',
  unknown: 'bg-bg text-muted',
};

/** The proposed artwork, addressed by slot — the GCS path never reaches here. */
function ProposedArtwork({ src, alt, filename }: { src: string; alt: string; filename: string | null }) {
  const [errored, setErrored] = useState(false);
  return (
    <a href={src} target="_blank" rel="noreferrer" className="flex shrink-0 flex-col items-center gap-0.5" title={filename || 'Open the uploaded artwork'}>
      {errored ? (
        <span className="flex h-14 w-14 items-center justify-center rounded-[8px] bg-danger/[0.08] p-1 text-center font-mono text-[8.5px] leading-tight text-danger">
          LOAD FAILED
        </span>
      ) : (
        <img
          src={src}
          alt={alt}
          onError={() => setErrored(true)}
          className="h-14 w-14 shrink-0 rounded-[8px] bg-bg object-contain"
          loading="lazy"
        />
      )}
      {filename && <span className="max-w-[64px] truncate text-[9.5px] text-subtle">{filename}</span>}
    </a>
  );
}

function ChangeRow({ change, proposal }: { change: ProposalChange; proposal: SlotProposal }) {
  const art = artworkFor(proposal, change.slotId);
  return (
    <li className="flex items-start gap-2 py-1">
      <Badge className={cn('mt-[1px] shrink-0', TONE[changeTone(change.changed)])}>{changeVerb(change.changed)}</Badge>
      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] leading-snug text-text">{change.summary}</p>
        <p className="text-[10px] text-subtle">
          {[change.slotId, change.code, change.view].filter(Boolean).join(' · ')}
          {!change.known && ' · location not in the registry'}
        </p>
      </div>
      {art?.imageUrl && (
        <ProposedArtwork src={art.imageUrl} alt={`Proposed artwork for ${change.label}`} filename={art.originalFilename} />
      )}
    </li>
  );
}

function ProposalCard({ proposal, onPromoted }: {
  proposal: SlotProposal;
  onPromoted: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const blocked = blockReason(proposal);
  // Artwork the request carried that no change row claimed — still shown: a
  // customer who uploaded a file expects a human to look at it.
  const orphanArt = artworkGallery(proposal).filter(
    (a) => !proposal.changes.some((c) => c.slotId === a.slotId),
  );

  async function promote() {
    if (!confirm(confirmMessage(proposal))) return;
    setBusy(true);
    try {
      await post(proposal.promoteUrl, {});
      toast.success(promotedMessage(proposal));
      await onPromoted();
    } catch (e) {
      toast.error(errMsg(e, 'Could not apply the requested changes'));
      // A refusal is usually "someone changed this tier first" — re-read so the
      // panel stops offering a promotion the server will keep refusing.
      await onPromoted();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-[10px] border border-warning/25 bg-surface p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[12px] font-medium">{proposalHeadline(proposal)}</p>
          <p className="text-[10px] text-muted">
            {[
              proposal.revisionNumber != null ? `revision #${proposal.revisionNumber}` : null,
              proposal.requestedAt ? `requested ${formatRelative(proposal.requestedAt)}` : null,
              proposal.source,
            ].filter(Boolean).join(' · ')}
          </p>
        </div>
        <Button size="sm" disabled={busy || !!blocked} onClick={promote}>
          <Check className="h-3.5 w-3.5" aria-hidden />
          {busy ? 'Applying…' : `Promote to tier ${proposal.tier}`}
        </Button>
      </div>

      <ul className="mt-1.5 divide-y divide-divider border-t border-divider pt-0.5">
        {proposal.changes.map((c, i) => (
          <ChangeRow key={`${c.slotId ?? 'x'}-${c.changed ?? 'x'}-${i}`} change={c} proposal={proposal} />
        ))}
      </ul>

      {orphanArt.length > 0 && (
        <div className="mt-1.5 border-t border-divider pt-1.5">
          <p className="text-[10px] font-medium uppercase tracking-[0.04em] text-muted">Uploaded with this request</p>
          <div className="mt-1 flex flex-wrap gap-2">
            {orphanArt.map((a) => (
              <ProposedArtwork key={a.slotId ?? a.fileField ?? a.sha256} src={a.imageUrl as string} alt="Proposed artwork" filename={a.originalFilename} />
            ))}
          </div>
        </div>
      )}

      {proposal.notes && (
        <div className="mt-1.5 border-t border-divider pt-1.5">
          <button
            type="button"
            onClick={() => setNotesOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[10.5px] font-medium text-primary hover:underline"
          >
            <MessageSquareQuote className="h-3 w-3" aria-hidden />
            {notesOpen ? 'Hide' : 'Show'} what the customer wrote
          </button>
          {notesOpen && (
            <p className="mt-1 whitespace-pre-wrap rounded-[8px] bg-bg/70 p-2 text-[11px] leading-snug text-text">
              {proposal.notes}
            </p>
          )}
        </div>
      )}

      {blocked ? (
        <p className="mt-1.5 flex items-start gap-1.5 rounded-[8px] bg-danger/[0.07] p-2 text-[10.5px] leading-snug text-danger">
          <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{blocked}</span>
        </p>
      ) : (
        <p className="mt-1.5 text-[10.5px] leading-snug text-muted">
          Promoting rewrites tier {proposal.tier}'s print locations and clears its preflight — use “Run preflight · all
          locations” on the tier {proposal.tier} card below before this order can be approved.
        </p>
      )}
    </div>
  );
}

/**
 * The whole panel. Renders nothing when there is no pending request, so the
 * drawer is unchanged for every submission that has none.
 */
export function SlotProposalPanel({ submissionId }: { submissionId: string }) {
  const { data, error } = useSWR<ProposalsPayload>(
    submissionId ? `/api/submissions/${encodeURIComponent(submissionId)}/proposals` : null,
    get,
  );

  // A failure to READ pending requests is shown, never swallowed: silence here
  // looks exactly like "no requests", and the customer would keep waiting.
  if (error) {
    return (
      <div className="rounded-[12px] border border-danger/30 bg-surface p-3 text-[11px] text-danger shadow-card">
        Could not load requested decoration changes — {errMsg(error, 'unknown error')}
      </div>
    );
  }

  const pending = pendingProposals(data);
  if (!pending.length) return null;

  // The submission itself AND this list: a promotion changes both, and a panel
  // still offering a request that already landed is worse than a slow one.
  const refresh = async () => {
    await Promise.all([
      refreshSubmission(submissionId),
      mutate(`/api/submissions/${encodeURIComponent(submissionId)}/proposals`),
    ]);
  };

  return (
    <div className="rounded-[12px] border border-warning/40 bg-warning/[0.05] p-3 shadow-card">
      <div className="mb-2 flex items-center gap-2">
        <Inbox className="h-4 w-4 text-warning" aria-hidden />
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-warning">
          Requested print-location changes · {pending.length} waiting
        </span>
      </div>
      <p className="mb-2 text-[10.5px] leading-snug text-muted">
        The customer asked for these from their proof email. They have <strong>not</strong> been applied — the request
        form can only record a decoration change, never make one.
      </p>
      {data?.registryError && (
        <p className="mb-2 text-[10.5px] text-danger">
          Decoration locations unavailable, so locations read as raw codes: {data.registryError}
        </p>
      )}
      <div className="space-y-2">
        {pending.map((p) => (
          <ProposalCard key={proposalKey(p)} proposal={p} onPromoted={refresh} />
        ))}
      </div>
    </div>
  );
}
